import React, { useState, useRef, useCallback, useMemo } from "react";
import dayjs from "dayjs";
import AssigneeGroup from "../../../components/AssigneeGroup";
import AssigneeHoverCard from "../../../components/AssigneeGroup/AssigneeHoverCard";
import { normalizeAssignees, isSoloView, isPrivateTask, canSeeAssignees } from "../../../components/AssigneeGroup/utils";
import PrivateBadge from "../../../components/PrivateBadge";
import AssigneeSelect from "../../../components/AssigneeSelect";
import { updateAssigneeStatus } from "../../../services/todoTask.service";
import { showErrorToast } from "../../../utils/Toaster/AppToast";
import {
  Typography,
  Popper,
  Paper,
  Stack,
  Avatar,
  List,
  ListItemText,
  ListItemButton,
  Autocomplete,
  TextField,
  InputAdornment,
  Box,
  ClickAwayListener,
  Tooltip,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { ISSUETYPE, PRIORITY, STATUS, TASK_STATUS } from "../../../constant/constant";
import { getUserPicker } from "../../../services/user.service";
import {
  FiUser, FiFlag, FiCalendar, FiCheckCircle,
  FiHash, FiUserCheck, FiClock,
  FiChevronDown, FiSearch,
  FiEyeOff,
} from "react-icons/fi";

const UI = {
  bg: "#FFFFFF",
  surface: "#F8FAFC",
  border: "#CBD5E1",
  borderLight: "#E2E8F0",
  text: "#0F172A",
  textSub: "#334155",
  textMuted: "#64748B",
  accent: "#0D9488",
  accentSoft: "#CCFBF1",
  high: "#DC2626",
  medium: "#D97706",
  low: "#16A34A",
};

const PRIORITY_CONFIG = {
  [PRIORITY.HIGH]: { label: "High", color: "#DC2626", bg: "#FEF2F2", text: "#991B1B" },
  [PRIORITY.MEDIUM]: { label: "Medium", color: "#D97706", bg: "#FFFBEB", text: "#92400E" },
  [PRIORITY.LOW]: { label: "Low", color: "#16A34A", bg: "#F0FDF4", text: "#14532D" },
};

const STATUS_CONFIG = {
  1: { label: "To Do",           bg: "#F1F5F9", text: "#475569", dot: "#94A3B8" },
  2: { label: "In Progress",     bg: "#EFF6FF", text: "#1D4ED8", dot: "#3B82F6" },
  3: { label: "Completed",       bg: "#F0FDF4", text: "#15803D", dot: "#22C55E" },
  4: { label: "Not Assigned",    bg: "#F0FDFA", text: "#0F766E", dot: "#0D9488" },
  5: { label: "Acknowledge",     bg: "#CFFAFE", text: "#0E7490", dot: "#06B6D4" },
  6: { label: "Rework Required", bg: "#FEE2E2", text: "#B91C1C", dot: "#EF4444" },
  7: { label: "Hold",            bg: "#FFF7ED", text: "#C2410C", dot: "#F97316" },
  8: { label: "Duplicate",       bg: "#F8FAFC", text: "#64748B", dot: "#94A3B8" },
};

// ── Assignee search — server-side via feedback-user-picker ───────────────────
const AssigneeSearchList = React.memo(({ onSelect, roles = "HR,PROJECT MANAGER" }) => {
  const [input, setInput] = useState("");
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const cursorRef = useRef(null);
  const searchRef = useRef("");
  const debounceRef = useRef(null);

  const fetchUsers = useCallback(async (search, cursor) => {
    const isPaging = !!cursor;
    isPaging ? setLoadingMore(true) : setLoading(true);
    try {
      const res = await getUserPicker({ search, cursor, limit: 20, roles });
      if (res?.error === false) {
        const items = res.data || [];
        setUsers((prev) => isPaging ? [...prev, ...items] : items);
        cursorRef.current = res.nextCursor;
        setHasMore(res.hasMore ?? false);
      }
    } catch { /* silent */ }
    finally { isPaging ? setLoadingMore(false) : setLoading(false); }
  }, [roles]);

  // Fetch initial list on mount
  const mountedRef = useRef(false);
  if (!mountedRef.current) { mountedRef.current = true; fetchUsers("", null); }

  const handleInput = useCallback((e) => {
    const val = e.target.value;
    setInput(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      searchRef.current = val;
      cursorRef.current = null;
      fetchUsers(val, null);
    }, 280);
  }, [fetchUsers]);

  const handleLoadMore = useCallback(() => {
    if (loadingMore || !hasMore || !cursorRef.current) return;
    fetchUsers(searchRef.current, cursorRef.current);
  }, [loadingMore, hasMore, fetchUsers]);

  return (
    <>
      <div style={{ padding: "8px 8px 4px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "6px 10px", borderRadius: 7, border: `1px solid ${UI.accent}`, background: UI.surface }}>
          <FiSearch size={13} color={UI.textMuted} />
          <input
            autoFocus
            placeholder="Search member…"
            value={input}
            onChange={handleInput}
            style={{ flex: 1, border: "none", outline: "none", background: "transparent", fontSize: "12.5px", color: UI.text, fontFamily: "inherit" }}
          />
          {loading && (
            <svg width="14" height="14" viewBox="0 0 14 14" style={{ flexShrink: 0, animation: "dpSpin 0.7s linear infinite" }}>
              <circle cx="7" cy="7" r="5.5" fill="none" stroke="#E2E8F0" strokeWidth="2" />
              <path d="M7 1.5 A5.5 5.5 0 0 1 12.5 7" fill="none" stroke={UI.accent} strokeWidth="2" strokeLinecap="round" />
              <style>{`@keyframes dpSpin { to { transform-origin: 7px 7px; transform: rotate(360deg); } }`}</style>
            </svg>
          )}
        </div>
      </div>
      <div
        style={{ maxHeight: 220, overflowY: "auto", padding: "0 4px 4px" }}
        onScroll={(e) => {
          const el = e.currentTarget;
          if (el.scrollHeight - el.scrollTop - el.clientHeight < 40) handleLoadMore();
        }}
      >
        {!loading && users.length === 0 ? (
          <div style={{ padding: "12px 8px", fontSize: 12, color: UI.textMuted, textAlign: "center", fontFamily: "'Inter', sans-serif" }}>
            No members found
          </div>
        ) : (
          users.map((user) => {
            const displayName = user.name || user.userName || "Unknown";
            return (
              <div
                key={user._id}
                onClick={(e) => onSelect(e, user)}
                style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 8px", borderRadius: 7, cursor: "pointer", marginBottom: 2 }}
                onMouseEnter={(e) => { e.currentTarget.style.background = UI.accentSoft; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
              >
                <div style={{ width: 24, height: 24, borderRadius: "50%", background: UI.accent, color: "#fff", fontSize: 10, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, backgroundImage: user.file ? `url(${user.file})` : "none", backgroundSize: "cover" }}>
                  {!user.file && displayName.charAt(0).toUpperCase()}
                </div>
                <span style={{ flex: 1, fontSize: 13, fontWeight: 500, color: UI.text, fontFamily: "'Inter', sans-serif" }}>{displayName}</span>
              </div>
            );
          })
        )}
        {loadingMore && (
          <div style={{ padding: "6px 8px", fontSize: 11, color: UI.textMuted, textAlign: "center", fontFamily: "'Inter', sans-serif" }}>Loading…</div>
        )}
      </div>
    </>
  );
});

// ── Row wrapper ───────────────────────────────────────────────────────────────
const PropertyRow = ({ icon: Icon, label, children, onClick, clickable = false }) => (
  <Box
    onClick={onClick}
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      px: 2,
      py: 1.1,
      gap: 1,
      cursor: clickable ? "pointer" : "default",
      borderRadius: "8px",
      mx: 0.5,
      transition: "background 0.13s",
      "&:hover": clickable ? { background: "#F1F5F9" } : {},
    }}
  >
    {/* Left label */}
    <Stack direction="row" alignItems="center" spacing={0.9} sx={{ flexShrink: 0, minWidth: 100 }}>
      {Icon && <Icon size={12} color={UI.textMuted} style={{ flexShrink: 0 }} />}
      <Typography
        sx={{
          fontSize: "11px",
          fontWeight: 600,
          color: UI.textMuted,
          textTransform: "uppercase",
          letterSpacing: "0.07em",
          fontFamily: "'Inter', sans-serif",
        }}
      >
        {label}
      </Typography>
    </Stack>

    {/* Right value */}
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", minWidth: 0, flex: 1 }}>
      {children}
    </Box>
  </Box>
);

// ── Dropdown popover ──────────────────────────────────────────────────────────
const DropdownPanel = ({ open, anchorEl, onClose, title, width = 200, children }) => (
  <Popper open={open} anchorEl={anchorEl} placement="bottom-end" sx={{ zIndex: 1400, mt: 0.5 }}>
    <ClickAwayListener onClickAway={onClose}>
      <Paper
        sx={{
          width,
          border: `1px solid ${UI.border}`,
          borderRadius: "10px",
          boxShadow: "0 8px 28px rgba(15,23,42,0.13)",
          overflow: "hidden",
        }}
      >
        <Stack
          direction="row" alignItems="center" justifyContent="space-between"
          sx={{ px: 1.5, py: 1, borderBottom: `1px solid ${UI.borderLight}`, background: UI.surface }}
        >
          <Typography sx={{ fontSize: "11px", fontWeight: 700, color: UI.textMuted, textTransform: "uppercase", letterSpacing: "0.07em", fontFamily: "'Inter', sans-serif" }}>
            {title}
          </Typography>
          <CloseIcon
            sx={{ fontSize: 15, color: UI.textMuted, cursor: "pointer", "&:hover": { color: UI.text } }}
            onClick={onClose}
          />
        </Stack>
        {children}
      </Paper>
    </ClickAwayListener>
  </Popper>
);

// ── Main component ────────────────────────────────────────────────────────────
const DetailsPanel = (props) => {
  const {
    selectedData,
    fromClient,
    parentTasks = [],
    isAdmin = false,
    userId = null,
    onSupportChange,
    onAcknowledgedByChange,
    onParentTaskChange,
    onPriorityChange,
    onVerifyStatusChange,
    onClientVerifyStatusChange,
    onStatusChange,
    statusOption = [],
    priorityOption = [],
    QAVerifyOption = [],
    clientVerifyOption = [],
    getCurrentStatusLabel,
    getCurrentPriorityLabel,
    getCurrentVerifyStatusLabel,
    getParentTaskName,
    onActualDeadlineDateChange,
    onAllowDeadlineModificationChange,
    onAcknowledge,
    onRequestRework,
    onUpdate,
    onAssigneesChange,
    onPrivateAssigneesChange,
  } = props;

  const creatorId = selectedData?.assignedBy?._id || selectedData?.assignedBy;
  const assigneeId = selectedData?.assignee?._id || selectedData?.assignee;
  const canEdit = isAdmin || (userId && creatorId && String(userId) === String(creatorId));

  const acknowledgedById = selectedData?.acknowledgedBy?._id || selectedData?.acknowledgedBy;
  const isAcknowledger = !!(userId && acknowledgedById && String(acknowledgedById) === String(userId));
  const showAcknowledgeActions = (isAcknowledger || isAdmin) && selectedData?.status === TASK_STATUS.COMPLETED;
  const canEditSupport = canEdit || (userId && assigneeId && String(userId) === String(assigneeId));

  const isDeadlineEditable = canEdit || !!selectedData?.allowDeadlineModification;
  const [dlg, setDlg] = useState({ open: false, pendingDate: null });
  const [dlgReason, setDlgReason] = useState("");
  const [dlgReasonError, setDlgReasonError] = useState("");

  const closeDlg = () => { setDlg({ open: false, pendingDate: null }); setDlgReason(""); setDlgReasonError(""); };

  const [openAssignee, setOpenAssignee] = useState(false);
  const [openSupport, setOpenSupport] = useState(false);
  const [openAcknowledgedBy, setOpenAcknowledgedBy] = useState(false);

  const [editingSerialNo, setEditingSerialNo] = useState(false);
  const [serialNoDraft, setSerialNoDraft] = useState("");
  const serialNoInputRef = useRef(null);

  const startEditSerialNo = () => {
    setSerialNoDraft(selectedData?.serialNo || "");
    setEditingSerialNo(true);
    setTimeout(() => serialNoInputRef.current?.select(), 0);
  };
  const saveSerialNo = () => {
    const trimmed = serialNoDraft.trim();
    if (trimmed !== (selectedData?.serialNo || "") && onUpdate) {
      onUpdate({ serialNo: trimmed || null });
    }
    setEditingSerialNo(false);
  };
  const cancelSerialNo = () => setEditingSerialNo(false);
  const [openStatus, setOpenStatus] = useState(false);
  const [openPriority,   setOpenPriority]   = useState(false);
  const [showReworkInput, setShowReworkInput] = useState(false);
  const [reworkNote,      setReworkNote]      = useState("");
  const [ackLoading,      setAckLoading]      = useState(false);

  const handleAcknowledgeClick = useCallback(async () => {
    setAckLoading(true);
    await onAcknowledge?.();
    setAckLoading(false);
  }, [onAcknowledge]);

  const handleReworkSubmit = useCallback(async () => {
    setAckLoading(true);
    await onRequestRework?.(reworkNote);
    setReworkNote("");
    setShowReworkInput(false);
    setAckLoading(false);
  }, [onRequestRework, reworkNote]);

  const assigneeRef = useRef(null);
  const supportRef = useRef(null);
  const acknowledgedByRef = useRef(null);
  const statusRef = useRef(null);
  const priorityRef = useRef(null);

  const people = useMemo(() => normalizeAssignees(selectedData), [selectedData]);
  const solo = useMemo(
    () => isSoloView(selectedData, { currentUserId: userId, isAdmin }),
    [selectedData, userId, isAdmin],
  );

  // The detail page is the one surface where per-assignee status should not be
  // hidden behind a hover, so this list is always visible and edits in place.
  const handleAssigneeStatusPick = useCallback(async (assigneeId2, nextStatus) => {
    if (!selectedData?._id) return;
    try {
      const res = await updateAssigneeStatus(selectedData._id, assigneeId2, nextStatus);
      if (res?.error === false) onUpdate?.({});
      else showErrorToast(res?.message || "Could not update status");
    } catch (err) {
      showErrorToast(err?.data?.message || err?.message || "Could not update status");
    }
  }, [selectedData, onUpdate]);

  const handleSupportSelect = useCallback((e, user) => {
    onSupportChange?.(e, user);
    setOpenSupport(false);
  }, [onSupportChange]);

  const handleAcknowledgedBySelect = useCallback((e, user) => {
    onAcknowledgedByChange?.(e, user);
    setOpenAcknowledgedBy(false);
  }, [onAcknowledgedByChange]);

  const showVerifyStatus =
    (selectedData?.status === STATUS.QA && fromClient === false) ||
    selectedData?.status === STATUS.PRODUCTION;

  const pCfg = PRIORITY_CONFIG[selectedData?.priority] || null;
  const sCfg = STATUS_CONFIG[selectedData?.status] || STATUS_CONFIG[1];

  const deadlineStr = selectedData?.actualDeadlineDate
    ? new Date(selectedData.actualDeadlineDate).toISOString().split("T")[0]
    : "";

  return (
    <Box
      sx={{
        background: UI.bg,
        border: `1.5px solid ${UI.borderLight}`,
        borderRadius: "14px",
        overflow: "hidden",
        boxShadow: "0 2px 12px rgba(15,23,42,0.06)",
      }}
    >
      {/* ── Header ── */}
      <Box
        sx={{
          px: 2,
          py: 1.5,
          background: "linear-gradient(135deg, #F8FAFC 0%, #F1F5F9 100%)",
          borderBottom: `1.5px solid ${UI.borderLight}`,
          display: "flex",
          alignItems: "center",
          gap: 1,
        }}
      >
        <Box
          sx={{
            width: 26, height: 26, borderRadius: "7px",
            background: UI.accentSoft,
            display: "flex", alignItems: "center", justifyContent: "center",
          }}
        >
          <FiCheckCircle size={13} color={UI.accent} />
        </Box>
        <Typography
          sx={{
            fontSize: "12px",
            fontWeight: 700,
            color: UI.textSub,
            letterSpacing: "0.06em",
            textTransform: "uppercase",
            fontFamily: "'Inter', sans-serif",
          }}
        >
          Properties
        </Typography>
      </Box>

      {/* ── Body ── */}
      <Box sx={{ py: 1 }}>
        {!fromClient && (
          <>
            {/* Task ID */}
            <PropertyRow icon={FiHash} label="Task ID">
              <Box
                sx={{
                  px: "9px", py: "3px",
                  borderRadius: "6px",
                  background: UI.accentSoft,
                  border: `1px solid ${UI.accent}30`,
                }}
              >
                <Typography
                  sx={{
                    fontSize: "12px", fontWeight: 700,
                    color: UI.accent,
                    fontFamily: "ui-monospace, SFMono-Regular, monospace",
                    letterSpacing: "0.04em",
                  }}
                >
                  {selectedData?.taskNo || "—"}
                </Typography>
              </Box>
            </PropertyRow>

            {/* S.No */}
            <PropertyRow icon={FiHash} label="S.No">
              {editingSerialNo ? (
                <TextField
                  inputRef={serialNoInputRef}
                  value={serialNoDraft}
                  onChange={(e) => setSerialNoDraft(e.target.value)}
                  onBlur={saveSerialNo}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") { e.preventDefault(); saveSerialNo(); }
                    if (e.key === "Escape") { e.preventDefault(); cancelSerialNo(); }
                  }}
                  size="small"
                  autoFocus
                  placeholder="e.g. 06-01"
                  sx={{
                    width: 100,
                    "& .MuiInputBase-input": {
                      fontSize: "12px", fontWeight: 700, py: "3px", px: "9px",
                      fontFamily: "ui-monospace, SFMono-Regular, monospace",
                      letterSpacing: "0.04em", color: "#0369A1",
                    },
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "6px",
                      "& fieldset": { borderColor: "#7DD3FC" },
                      "&:hover fieldset": { borderColor: "#38BDF8" },
                      "&.Mui-focused fieldset": { borderColor: "#0284C7" },
                    },
                  }}
                />
              ) : (
                <Tooltip
                  title={canEdit ? "Click to edit S.No" : "Monthly serial number — auto-generated per month (MM-count). Resets at the start of each month."}
                  placement="left"
                  arrow
                >
                  <Box
                    onClick={canEdit ? startEditSerialNo : undefined}
                    sx={{
                      px: "9px", py: "3px",
                      borderRadius: "6px",
                      background: "#F0F9FF",
                      border: "1px solid #BAE6FD",
                      cursor: canEdit ? "text" : "default",
                      minWidth: 36,
                      "&:hover": canEdit ? { borderColor: "#38BDF8", background: "#E0F2FE" } : {},
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "12px", fontWeight: 700,
                        color: selectedData?.serialNo ? "#0369A1" : "#94A3B8",
                        fontFamily: "ui-monospace, SFMono-Regular, monospace",
                        letterSpacing: "0.04em",
                      }}
                    >
                      {selectedData?.serialNo || (canEdit ? "—" : "—")}
                    </Typography>
                  </Box>
                </Tooltip>
              )}
            </PropertyRow>

            {/* Assignees */}
            <PropertyRow icon={FiUser} label="Assignees" clickable={canEdit} onClick={canEdit ? () => setOpenAssignee(true) : undefined}>
              <Stack direction="row" alignItems="center" spacing={0.8} ref={assigneeRef}>
                <AssigneeGroup
                  task={selectedData}
                  taskId={selectedData?._id}
                  assignedBy={creatorId}
                  size={24}
                  max={4}
                  placement="left"
                  isAdmin={isAdmin}
                  currentUserId={userId}
                  onChanged={() => onUpdate?.({})}
                />
                <Typography sx={{ fontSize: "13px", fontWeight: 600, color: UI.text, lineHeight: 1.2, fontFamily: "'Inter', sans-serif" }}>
                  {solo
                    ? "You"
                    : people.length === 0
                    ? "Unassigned"
                    : people.length === 1
                    ? people[0].name
                    : `${people.length} assignees`}
                </Typography>
                {canSeeAssignees(selectedData, { currentUserId: userId, isAdmin }) &&
                  isPrivateTask(selectedData) && <PrivateBadge compact />}
                {canEdit && <FiChevronDown size={13} color={UI.textMuted} style={{ flexShrink: 0 }} />}
              </Stack>
            </PropertyRow>

            {/* Persistent per-assignee breakdown — no hover required here. */}
            {people.length > 0 && (
              <Box sx={{ px: 2, pb: 1.2 }}>
                <AssigneeHoverCard
                  variant="inline"
                  title="Completion"
                  solo={solo}
                  assignees={people}
                  canEdit={(a) => canEdit || String(a._id) === String(userId)}
                  pendingId={null}
                  onPickStatus={handleAssigneeStatusPick}
                />
              </Box>
            )}

            {canEdit && !fromClient && (
              <PropertyRow icon={FiEyeOff} label="Assignee privacy">
                <Tooltip
                  title={selectedData?.privateAssignees
                    ? "Uncheck to let assignees see each other on this task"
                    : "Each assignee will only see their own line — no other names, avatars or counts"}
                  placement="top"
                >
                  <Stack
                    direction="row" alignItems="center" spacing={0.3}
                    sx={{ cursor: "pointer" }}
                    onClick={() => onPrivateAssigneesChange?.(!selectedData?.privateAssignees)}
                  >
                    <Checkbox
                      size="small"
                      checked={!!selectedData?.privateAssignees}
                      onChange={(e) => onPrivateAssigneesChange?.(e.target.checked)}
                      sx={{ p: 0.2, color: UI.textMuted, "&.Mui-checked": { color: UI.accent } }}
                    />
                    <Typography sx={{ fontSize: "10px", fontWeight: 600, color: selectedData?.privateAssignees ? UI.accent : UI.textMuted, userSelect: "none", whiteSpace: "nowrap" }}>
                      Keep assignee info private
                    </Typography>
                  </Stack>
                </Tooltip>
              </PropertyRow>
            )}

            {canEdit && (
              <DropdownPanel
                open={openAssignee}
                anchorEl={assigneeRef.current}
                onClose={() => setOpenAssignee(false)}
                title="Select Assignees"
                width={280}
              >
                {openAssignee && (
                  <Box sx={{ p: 1 }}>
                    <AssigneeSelect
                      roles="HR,PROJECT MANAGER"
                      value={people.map((p) => p._id)}
                      preloaded={people.map((p) => ({ _id: p._id, label: p.name, file: p.avatar }))}
                      onChange={(ids) => onAssigneesChange?.(ids)}
                      placeholder="Search members…"
                      emptyLabel="Add assignees"
                    />
                  </Box>
                )}
              </DropdownPanel>
            )}

            {/* Support */}
            <PropertyRow icon={FiUser} label="Support" clickable={canEditSupport} onClick={canEditSupport ? () => setOpenSupport(true) : undefined}>
              <Stack direction="row" alignItems="center" spacing={0.8} ref={supportRef}>
                {selectedData?.supportName ? (
                  <>
                    <Avatar
                      src={selectedData?.supportInfo?.file}
                      sx={{
                        width: 24, height: 24, fontSize: "10px", fontWeight: 700,
                        bgcolor: "#7C3AED", color: "#fff",
                        border: "2px solid #EDE9FE",
                      }}
                    >
                      {(selectedData.supportName || "?").charAt(0).toUpperCase()}
                    </Avatar>
                    <Typography sx={{ fontSize: "13px", fontWeight: 600, color: UI.text, fontFamily: "'Inter', sans-serif" }}>
                      {selectedData.supportName}
                    </Typography>
                  </>
                ) : (
                  <Typography sx={{ fontSize: "12.5px", color: UI.textMuted, fontStyle: "italic", fontFamily: "'Inter', sans-serif" }}>
                    Not assigned
                  </Typography>
                )}
                {canEditSupport && <FiChevronDown size={13} color={UI.textMuted} style={{ flexShrink: 0 }} />}
              </Stack>
            </PropertyRow>

            {canEditSupport && (
              <DropdownPanel
                open={openSupport}
                anchorEl={supportRef.current}
                onClose={() => setOpenSupport(false)}
                title="Select Support"
                width={240}
              >
                {openSupport && (
                  <AssigneeSearchList onSelect={handleSupportSelect} />
                )}
              </DropdownPanel>
            )}

            {/* Acknowledged By */}
            <PropertyRow icon={FiUserCheck} label="Acknowledged by" clickable={canEdit} onClick={() => canEdit && setOpenAcknowledgedBy(true)}>
              <Stack direction="row" alignItems="center" spacing={0.8} ref={acknowledgedByRef}>
                {selectedData?.acknowledgedBy ? (
                  <Tooltip title={selectedData.acknowledgedByName ? `Acknowledged by: ${selectedData.acknowledgedByName}` : ""} placement="top" arrow>
                    <Stack direction="row" alignItems="center" spacing={0.8}>
                      <Avatar
                        src={selectedData?.acknowledgedByInfo?.file}
                        sx={{ width: 18, height: 18, fontSize: "9px", bgcolor: UI.accent, color: "#fff", fontWeight: 700 }}
                      >
                        {(selectedData.acknowledgedByName || "?").charAt(0).toUpperCase()}
                      </Avatar>
                      <Typography sx={{ fontSize: "13px", fontWeight: 600, color: UI.text, fontFamily: "'Inter', sans-serif" }}>
                        {selectedData.acknowledgedByName}
                      </Typography>
                    </Stack>
                  </Tooltip>
                ) : (
                  <Typography sx={{ fontSize: "12.5px", color: UI.textMuted, fontStyle: "italic", fontFamily: "'Inter', sans-serif" }}>
                    Not acknowledged
                  </Typography>
                )}
                {canEdit && <FiChevronDown size={13} color={UI.textMuted} style={{ flexShrink: 0 }} />}
              </Stack>
            </PropertyRow>

            {canEdit && (
              <DropdownPanel
                open={openAcknowledgedBy}
                anchorEl={acknowledgedByRef.current}
                onClose={() => setOpenAcknowledgedBy(false)}
                title="Select Acknowledged By"
                width={240}
              >
                {openAcknowledgedBy && (
                  <AssigneeSearchList onSelect={handleAcknowledgedBySelect} />
                )}
              </DropdownPanel>
            )}

            {/* Created By */}
            <PropertyRow icon={FiUserCheck} label="Created by">
              <Box sx={{ textAlign: "right" }}>
                <Typography sx={{ fontSize: "13px", fontWeight: 600, color: UI.text, fontFamily: "'Inter', sans-serif" }}>
                  {selectedData?.creatorName || "—"}
                </Typography>
                {selectedData?.creatorRoleName && (
                  <Typography sx={{ fontSize: "10.5px", color: UI.textMuted, fontFamily: "'Inter', sans-serif" }}>
                    {selectedData.creatorRoleName}
                  </Typography>
                )}
              </Box>
            </PropertyRow>

            {/* Deadline */}
            <Box>
              <PropertyRow icon={FiCalendar} label="Deadline">
                <Stack direction="row" alignItems="center" spacing={1}>
                  {/* Allow Modification checkbox — creator/admin only */}
                  {canEdit && !fromClient && (
                    <Tooltip
                      title={selectedData?.allowDeadlineModification ? "Uncheck to lock deadline for others" : "Allow other participants to modify the deadline"}
                      placement="top"
                    >
                      <Stack
                        direction="row" alignItems="center" spacing={0.3}
                        sx={{ cursor: "pointer" }}
                        onClick={() => onAllowDeadlineModificationChange?.(!selectedData?.allowDeadlineModification)}
                      >
                        <Checkbox
                          size="small"
                          checked={!!selectedData?.allowDeadlineModification}
                          onChange={(e) => onAllowDeadlineModificationChange?.(e.target.checked)}
                          sx={{ p: 0.2, color: UI.textMuted, "&.Mui-checked": { color: UI.accent } }}
                        />
                        <Typography sx={{ fontSize: "10px", fontWeight: 600, color: selectedData?.allowDeadlineModification ? UI.accent : UI.textMuted, userSelect: "none", whiteSpace: "nowrap" }}>
                          Allow Edit
                        </Typography>
                      </Stack>
                    </Tooltip>
                  )}
                  <Box
                    sx={{
                      display: "flex", alignItems: "center", gap: "6px",
                      px: "10px", py: "5px",
                      borderRadius: "7px",
                      border: `1.5px solid ${UI.borderLight}`,
                      background: UI.surface,
                      cursor: (fromClient || !isDeadlineEditable) ? "default" : "pointer",
                      "&:hover": (fromClient || !isDeadlineEditable) ? {} : { border: `1.5px solid ${UI.accent}`, background: UI.accentSoft },
                      transition: "all 0.15s",
                      opacity: (fromClient || !isDeadlineEditable) ? 0.65 : 1,
                    }}
                  >
                    <TextField
                      type="date"
                      value={deadlineStr}
                      onChange={(e) => {
                        if (fromClient || !isDeadlineEditable) return;
                        const newDate = e.target.value;
                        if (newDate !== deadlineStr) setDlg({ open: true, pendingDate: newDate });
                      }}
                      disabled={fromClient || !isDeadlineEditable}
                      variant="standard"
                      InputProps={{
                        disableUnderline: true,
                        sx: {
                          fontSize: "12.5px", fontWeight: 600, color: UI.text,
                          "& input": { cursor: (fromClient || !isDeadlineEditable) ? "default" : "pointer", py: 0, px: 0, textAlign: "right" },
                        },
                      }}
                      inputProps={{ min: new Date().toISOString().split("T")[0] }}
                      sx={{ "& .MuiInputBase-root": { background: "transparent" } }}
                    />
                  </Box>
                </Stack>
              </PropertyRow>

              {/* Deadline change dialog */}
              <Dialog open={dlg.open} onClose={closeDlg} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: "14px" } }}>
                <DialogTitle sx={{ fontSize: "15px", fontWeight: 700, pb: 1 }}>Change Deadline</DialogTitle>
                <DialogContent>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2, px: 1.5, py: 1, borderRadius: "8px", bgcolor: UI.surface, border: `1px solid ${UI.borderLight}` }}>
                    <Typography sx={{ fontSize: "12.5px", color: UI.textMuted }}>
                      {deadlineStr ? dayjs(deadlineStr).format("D MMM YYYY") : "Not set"}
                    </Typography>
                    <Typography sx={{ fontSize: "12px", color: UI.textMuted }}>→</Typography>
                    <Typography sx={{ fontSize: "12.5px", fontWeight: 700, color: UI.accent }}>
                      {dlg.pendingDate ? dayjs(dlg.pendingDate).format("D MMM YYYY") : "—"}
                    </Typography>
                  </Stack>
                  <Typography sx={{ fontSize: "11px", fontWeight: 700, color: dlgReasonError ? "#EF4444" : UI.textMuted, textTransform: "uppercase", letterSpacing: "0.07em", mb: 0.8 }}>
                    Reason for Change
                    {!canEdit
                      ? <Box component="span" sx={{ color: "#EF4444", ml: 0.3 }}>*</Box>
                      : <Box component="span" sx={{ fontSize: "10px", fontWeight: 500, color: UI.textMuted, ml: 0.5, textTransform: "none", letterSpacing: 0 }}>(Optional)</Box>
                    }
                  </Typography>
                  <Box
                    component="textarea"
                    value={dlgReason}
                    onChange={(e) => { setDlgReason(e.target.value); if (e.target.value.trim()) setDlgReasonError(""); }}
                    placeholder="e.g. Client requested an extension…"
                    rows={3}
                    sx={{
                      width: "100%", boxSizing: "border-box",
                      border: `1.5px solid ${dlgReasonError ? "#EF4444" : UI.borderLight}`,
                      borderRadius: "8px", padding: "10px 12px",
                      fontSize: "13px", fontFamily: "'Inter', sans-serif",
                      color: UI.text, background: UI.surface,
                      outline: "none", resize: "vertical", display: "block",
                      transition: "border-color 0.2s",
                      "&:focus": { borderColor: dlgReasonError ? "#EF4444" : UI.accent, boxShadow: "0 0 0 3px rgba(13,148,136,0.1)" },
                      "&::placeholder": { color: UI.textMuted },
                    }}
                  />
                  {dlgReasonError && (
                    <Typography sx={{ fontSize: "11px", color: "#EF4444", mt: 0.5, fontWeight: 500 }}>{dlgReasonError}</Typography>
                  )}
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
                  <Button onClick={closeDlg} sx={{ color: UI.textSub, textTransform: "none", fontWeight: 600, borderRadius: "8px" }}>
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    onClick={() => {
                      if (!canEdit && !dlgReason.trim()) { setDlgReasonError("Reason is required"); return; }
                      onActualDeadlineDateChange?.({ target: { value: dlg.pendingDate } }, dlgReason.trim() || undefined);
                      closeDlg();
                    }}
                    sx={{ textTransform: "none", fontWeight: 700, bgcolor: UI.accent, "&:hover": { bgcolor: "#0F766E" }, borderRadius: "8px" }}
                  >
                    Confirm
                  </Button>
                </DialogActions>
              </Dialog>
            </Box>
          </>
        )}

        {/* Divider */}
        <Box sx={{ height: "1px", background: UI.borderLight, mx: 2, my: 0.5 }} />

        {/* Status */}
        <PropertyRow icon={FiCheckCircle} label="Status" clickable={!fromClient && statusOption?.length > 0} onClick={(!fromClient && statusOption?.length > 0) ? () => setOpenStatus(true) : undefined}>
          <Stack direction="row" alignItems="center" spacing={0.5} ref={statusRef}>
            <Box
              sx={{
                display: "inline-flex", alignItems: "center", gap: "6px",
                px: "10px", py: "4px",
                borderRadius: "20px",
                background: sCfg.bg,
              }}
            >
              <Box sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: sCfg.dot, flexShrink: 0 }} />
              <Typography sx={{ fontSize: "12px", fontWeight: 700, color: sCfg.text, fontFamily: "'Inter', sans-serif" }}>
                {getCurrentStatusLabel?.() || "—"}
              </Typography>
            </Box>
            {!fromClient && statusOption?.length > 0 && <FiChevronDown size={13} color={UI.textMuted} style={{ flexShrink: 0 }} />}
          </Stack>
        </PropertyRow>

        {!fromClient && statusOption?.length > 0 && (
          <DropdownPanel open={openStatus} anchorEl={statusRef.current} onClose={() => setOpenStatus(false)} title="Change Status" width={180}>
            <List sx={{ p: 0.5 }}>
              {statusOption.map((opt) => {
                const sc = STATUS_CONFIG[opt.value] || STATUS_CONFIG[1];
                return (
                  <ListItemButton
                    key={opt.value}
                    onClick={() => { onStatusChange?.(null, opt); setOpenStatus(false); }}
                    sx={{ py: 0.8, px: 1.2, borderRadius: "7px", mb: "2px", "&:hover": { background: UI.accentSoft } }}
                  >
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: sc.dot, flexShrink: 0 }} />
                      <ListItemText primary={opt.label} primaryTypographyProps={{ sx: { fontSize: "13px", fontWeight: 500, color: UI.text, fontFamily: "'Inter', sans-serif" } }} />
                    </Stack>
                  </ListItemButton>
                );
              })}
            </List>
          </DropdownPanel>
        )}

        {/* Rework note banner */}
        {selectedData?.status === TASK_STATUS.REWORK_REQUIRED && selectedData?.acknowledgementNote && (
          <Box sx={{ mx: 2, mb: 1, mt: 0.5, p: "10px 14px", borderRadius: "10px", bgcolor: "#FFF7ED", border: "1px solid #FED7AA" }}>
            <Stack direction="row" spacing={1} alignItems="flex-start">
              <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: "#F97316", mt: "5px", flexShrink: 0 }} />
              <Box>
                <Typography sx={{ fontSize: "10px", fontWeight: 700, color: "#C2410C", textTransform: "uppercase", letterSpacing: "0.06em", mb: 0.3 }}>
                  Rework Required
                </Typography>
                <Typography sx={{ fontSize: "12px", color: "#92400E", lineHeight: 1.5 }}>
                  {selectedData.acknowledgementNote}
                </Typography>
                {selectedData?.acknowledgedByName && (
                  <Typography sx={{ fontSize: "10px", color: "#C2410C", mt: 0.5, fontWeight: 600 }}>
                    — {selectedData.acknowledgedByName}
                  </Typography>
                )}
              </Box>
            </Stack>
          </Box>
        )}

        {/* Priority */}
        <PropertyRow icon={FiFlag} label="Priority" clickable={canEdit} onClick={canEdit ? () => setOpenPriority(true) : undefined}>
          <Stack direction="row" alignItems="center" spacing={0.5} ref={priorityRef}>
            {pCfg ? (
              <Box
                sx={{
                  display: "inline-flex", alignItems: "center", gap: "6px",
                  px: "10px", py: "4px",
                  borderRadius: "20px",
                  background: pCfg.bg,
                }}
              >
                <Box sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: pCfg.color, flexShrink: 0 }} />
                <Typography sx={{ fontSize: "12px", fontWeight: 700, color: pCfg.text, fontFamily: "'Inter', sans-serif" }}>
                  {pCfg.label}
                </Typography>
              </Box>
            ) : (
              <Typography sx={{ fontSize: "12.5px", color: UI.textMuted }}>—</Typography>
            )}
            {canEdit && <FiChevronDown size={13} color={UI.textMuted} style={{ flexShrink: 0 }} />}
          </Stack>
        </PropertyRow>

        {canEdit && (
          <DropdownPanel open={openPriority} anchorEl={priorityRef.current} onClose={() => setOpenPriority(false)} title="Change Priority" width={160}>
            <List sx={{ p: 0.5 }}>
              {priorityOption.map((opt) => {
                const pc = PRIORITY_CONFIG[opt.value];
                return (
                  <ListItemButton
                    key={opt.value}
                    onClick={() => { onPriorityChange?.(null, opt); setOpenPriority(false); }}
                    sx={{ py: 0.8, px: 1.2, borderRadius: "7px", mb: "2px", "&:hover": { background: UI.accentSoft } }}
                  >
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: pc?.color || UI.textMuted, flexShrink: 0 }} />
                      <ListItemText primary={opt.label} primaryTypographyProps={{ sx: { fontSize: "13px", fontWeight: 500, color: UI.text, fontFamily: "'Inter', sans-serif" } }} />
                    </Stack>
                  </ListItemButton>
                );
              })}
            </List>
          </DropdownPanel>
        )}

        {/* Verify Status */}
        {showVerifyStatus && (
          <PropertyRow icon={FiCheckCircle} label="Verify status">
            <Box sx={{ minWidth: 140 }}>
              {selectedData?.clientVerified ? (
                <Box sx={{ display: "inline-flex", alignItems: "center", gap: "6px", px: "10px", py: "4px", borderRadius: "20px", background: "#F0FDF4" }}>
                  <Box sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: "#22C55E" }} />
                  <Typography sx={{ fontSize: "12px", fontWeight: 700, color: "#15803D", fontFamily: "'Inter', sans-serif" }}>Completed</Typography>
                </Box>
              ) : (
                <Autocomplete
                  options={fromClient === true ? clientVerifyOption : QAVerifyOption}
                  getOptionLabel={(option) => option.label || ""}
                  disableClearable
                  value={clientVerifyOption.find((o) => o.label === getCurrentVerifyStatusLabel?.()) || null}
                  onChange={fromClient === true ? onClientVerifyStatusChange : onVerifyStatusChange}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="standard"
                      InputProps={{
                        ...params.InputProps,
                        disableUnderline: true,
                        sx: { fontSize: "13px", fontWeight: 600, color: UI.low, "& input": { textAlign: "right", cursor: "pointer" } },
                      }}
                    />
                  )}
                  sx={{ "& .MuiAutocomplete-inputRoot": { padding: "0 !important", background: "transparent" } }}
                />
              )}
            </Box>
          </PropertyRow>
        )}
      </Box>

      {/* ── Acknowledge action section ── */}
      {showAcknowledgeActions && (
        <Box sx={{ px: 2, pt: 1.5, pb: 1, borderTop: `1.5px solid ${UI.borderLight}`, bgcolor: "#F0FDFA" }}>
          <Stack direction="row" alignItems="center" spacing={0.8} sx={{ mb: 1.2 }}>
            <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: "#0D9488", flexShrink: 0 }} />
            <Typography sx={{ fontSize: "11px", fontWeight: 700, color: "#0F766E", textTransform: "uppercase", letterSpacing: "0.06em" }}>
              Your Acknowledgment Required
            </Typography>
          </Stack>

          {!showReworkInput ? (
            <Stack direction="row" spacing={1}>
              <button
                type="button"
                disabled={ackLoading}
                onClick={handleAcknowledgeClick}
                style={{
                  flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                  border: "none", borderRadius: "9px",
                  background: "linear-gradient(135deg, #0D9488, #0F766E)",
                  color: "#fff", fontSize: "12px", fontWeight: 700,
                  padding: "9px 0", cursor: ackLoading ? "not-allowed" : "pointer",
                  fontFamily: "inherit", opacity: ackLoading ? 0.7 : 1,
                  boxShadow: "0 2px 8px rgba(13,148,136,0.30)",
                }}
              >
                ✓ Acknowledge
              </button>
              <button
                type="button"
                disabled={ackLoading}
                onClick={() => setShowReworkInput(true)}
                style={{
                  flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                  border: "1.5px solid #FCD34D", borderRadius: "9px",
                  background: "#FFFBEB", color: "#92400E",
                  fontSize: "12px", fontWeight: 700,
                  padding: "9px 0", cursor: "pointer", fontFamily: "inherit",
                }}
              >
                ↩ Request Rework
              </button>
            </Stack>
          ) : (
            <Box>
              <Typography sx={{ fontSize: "11px", fontWeight: 700, color: "#92400E", mb: 0.8 }}>
                Describe what needs to be fixed (optional)
              </Typography>
              <Box
                component="textarea"
                value={reworkNote}
                onChange={(e) => setReworkNote(e.target.value)}
                placeholder="e.g. Login button alignment is off on mobile…"
                rows={3}
                sx={{
                  width: "100%", boxSizing: "border-box",
                  border: "1.5px solid #FCD34D", borderRadius: "8px",
                  p: "8px 10px", fontSize: "12px", color: "#0F172A",
                  fontFamily: "inherit", resize: "vertical", outline: "none",
                  bgcolor: "#FFFBEB",
                  "&:focus": { borderColor: "#F59E0B" },
                }}
              />
              <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                <button
                  type="button"
                  onClick={() => { setShowReworkInput(false); setReworkNote(""); }}
                  style={{
                    flex: 1, border: "1px solid #E2E8F0", borderRadius: "8px",
                    background: "#fff", color: "#475569",
                    fontSize: "12px", fontWeight: 600, padding: "8px 0",
                    cursor: "pointer", fontFamily: "inherit",
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  disabled={ackLoading}
                  onClick={handleReworkSubmit}
                  style={{
                    flex: 2, border: "none", borderRadius: "8px",
                    background: "#F59E0B", color: "#fff",
                    fontSize: "12px", fontWeight: 700, padding: "8px 0",
                    cursor: ackLoading ? "not-allowed" : "pointer",
                    fontFamily: "inherit", opacity: ackLoading ? 0.7 : 1,
                  }}
                >
                  {ackLoading ? "Sending…" : "Send Rework Request"}
                </button>
              </Stack>
            </Box>
          )}
        </Box>
      )}

      {/* ── Footer ── */}
      <Box
        sx={{
          px: 2, py: 1.2,
          borderTop: `1.5px solid ${UI.borderLight}`,
          background: UI.surface,
          display: "flex",
          alignItems: "center",
          gap: 0.8,
        }}
      >
        <FiClock size={11} color={UI.textMuted} />
        <Typography sx={{ fontSize: "11px", color: UI.textMuted, fontFamily: "'Inter', sans-serif" }}>
          Updated{" "}
          {selectedData?.updatedAt
            ? new Date(selectedData.updatedAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
            : "—"}
        </Typography>
      </Box>
    </Box>
  );
};

export default DetailsPanel;
